import { useEffect, useRef, useState } from 'react';
import Navbar from './Navbar';
import NotificationToast from './NotificationToast';
import ShutdownOverlay from './ShutdownOverlay';
import { useGameStore } from '../stores/gameStore';
import { useTransferStore, justCompletedIds } from '../stores/transferStore';
import { useNotificationStore } from '../stores/notificationStore';
import { softwareApi, fileSystemApi } from '../services/api';
import { Player } from '../types';

interface LayoutProps {
  children: React.ReactNode;
}

const TICK_MS  = 100;
const TICK_SEC = TICK_MS / 1000;

// Encrypted-HD backups complete on a timer (readyAt), not through the transfer
// store, so they get no completion toast on their own. Track backups seen as
// in-progress and fire a one-time notification when each crosses readyAt — never
// for backups that were already finished when the page loaded.
const seenPendingBackups = new Set<string>();
const notifiedBackups    = new Set<string>();

function checkBackupCompletions(player: Player) {
  const hd = player.encryptedHd;
  if (!hd) return;
  const now  = Date.now();
  const push = useNotificationStore.getState().push;

  const items = [
    ...hd.software.map(s => ({
      key: `hd-sw-${s.type}-${s.readyAt}`,
      readyAt: s.readyAt,
      message: `${s.type} v${s.version.toFixed(1)} backed up to the encrypted HD.`,
    })),
    ...hd.files.map(f => ({
      key: `hd-file-${f.name}-${f.readyAt}`,
      readyAt: f.readyAt,
      message: `${f.name} backed up to the encrypted HD.`,
    })),
  ];

  for (const it of items) {
    if (new Date(it.readyAt).getTime() > now) { seenPendingBackups.add(it.key); continue; }
    if (seenPendingBackups.has(it.key) && !notifiedBackups.has(it.key)) {
      notifiedBackups.add(it.key);
      seenPendingBackups.delete(it.key);
      push({
        id: it.key,
        type: 'backup_complete',
        title: 'BACKUP COMPLETE',
        message: it.message,
        createdAt: new Date().toISOString(),
      });
    }
  }
}

export default function Layout({ children }: LayoutProps) {
  const loadPlayer = useGameStore(s => s.loadPlayer);
  const playerRef  = useRef(useGameStore.getState().player);
  const tickAllRef = useRef(useTransferStore.getState().tickAll);

  // Detect that a newer build has been deployed while this SPA stays loaded: poll the
  // freshly-served version.json and compare with the version baked into this bundle.
  const [outdated, setOutdated] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && typeof data?.version === 'string' && data.version !== __APP_VERSION__) {
          setOutdated(true);
        }
      } catch { /* offline, or version.json absent (dev) — ignore */ }
    };
    check();
    const id = setInterval(check, 60_000);
    const onFocus = () => check();
    window.addEventListener('focus', onFocus);
    return () => { cancelled = true; clearInterval(id); window.removeEventListener('focus', onFocus); };
  }, []);

  useEffect(() => {
    const u1 = useGameStore.subscribe(s    => { playerRef.current  = s.player; });
    const u2 = useTransferStore.subscribe(s => { tickAllRef.current = s.tickAll; });
    return () => { u1(); u2(); };
  }, []);

  useEffect(() => {
    const id = setInterval(async () => {
      const player = playerRef.current;
      if (!player) return;

      // Fire a toast when an encrypted-HD backup finishes uploading.
      checkBackupCompletions(player);

      // Snapshot transfers BEFORE tick so we can read names/versions of completed ones.
      const snapshot = useTransferStore.getState().transfers;
      const { completedSoftware, completedFiles } = tickAllRef.current(TICK_SEC, player.internetSpeedMBit);
      if (completedSoftware.length === 0 && completedFiles.length === 0) return;

      // Confirm software installs
      await Promise.allSettled(completedSoftware.map(sid => softwareApi.confirmInstall(sid)));

      // Save completed text-file downloads into player profile
      await Promise.allSettled(
        completedFiles.map(f => fileSystemApi.completeDownload(f.fileName, f.content, f.sizeMb, f.id))
      );

      // Push download-complete notifications
      const { push } = useNotificationStore.getState();
      const now = new Date().toISOString();

      completedSoftware.forEach(sid => {
        const t = snapshot.find(x => x.softwareId === sid);
        push({
          id: `sw-done-${sid}`,
          type: 'download_complete',
          title: 'DOWNLOAD COMPLETE',
          message: t
            ? `${t.name}${t.version != null ? ` v${t.version.toFixed(1)}` : ''} (${t.fileSizeMb.toFixed(1)} MB) installed.`
            : 'Software installed successfully.',
          createdAt: now,
        });
      });

      completedFiles.forEach(f => {
        push({
          id: `file-done-${f.id}`,
          type: 'download_complete',
          title: 'FILE DOWNLOAD COMPLETE',
          message: `${f.fileName} (${f.sizeMb.toFixed(2)} MB) saved to local drive.`,
          createdAt: now,
        });
      });

      await loadPlayer();

      // Release the completion guards now that the backend has confirmed and the
      // fresh player (isDownloaded=true) has been loaded. Leaving them set would
      // permanently block restoreDownloads from re-creating a transfer for a LATER
      // download/upgrade of the SAME id — the reported "start a new upgrade right
      // after finishing one → no progress bar and it never completes" bug (a reload
      // recreated the module and cleared the set, which is why it worked again).
      completedSoftware.forEach(sid => {
        justCompletedIds.delete(`sw-${sid}`);
        const tid = snapshot.find(x => x.softwareId === sid)?.id;
        if (tid) justCompletedIds.delete(tid);
      });
      completedFiles.forEach(f => justCompletedIds.delete(f.id));
    }, TICK_MS);

    return () => clearInterval(id);
  }, [loadPlayer]);

  // Light periodic refresh so a node shut down (or freed) by someone else is
  // picked up promptly even while the player is idle.
  useEffect(() => {
    const id = setInterval(() => {
      if (playerRef.current) loadPlayer();
    }, 15000);
    return () => clearInterval(id);
  }, [loadPlayer]);

  return (
    <div className="app">
      <Navbar />
      {outdated && (
        <div
          role="alert"
          style={{
            background: '#7c2d12',
            borderBottom: '1px solid #f97316',
            color: '#fed7aa',
            padding: '0.5rem 1rem',
            textAlign: 'center',
            fontFamily: 'monospace',
            fontSize: '0.85rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.75rem',
            flexWrap: 'wrap',
          }}
        >
          <span>⚠ You're running an outdated version of the game. Refresh to get the latest update.</span>
          <button
            onClick={() => window.location.reload()}
            style={{
              background: '#f97316',
              color: '#000',
              border: 'none',
              borderRadius: '4px',
              padding: '0.25rem 0.8rem',
              cursor: 'pointer',
              fontFamily: 'monospace',
              fontWeight: 'bold',
            }}
          >
            Refresh now
          </button>
        </div>
      )}
      <main className="main-content">
        {children}
      </main>
      {/* Version footer: in normal flow at the end of the page (not fixed), so it
          scrolls with the content and is only seen when you reach the bottom. */}
      <footer
        aria-hidden
        style={{
          textAlign: 'center',
          fontFamily: 'monospace',
          fontSize: '0.72rem',
          letterSpacing: '0.04em',
          color: 'var(--text-color)',
          opacity: 0.8,
          padding: '1rem 0 1.25rem',
          marginTop: '2rem',
          borderTop: '1px solid var(--border-color)',
        }}
      >
        {__APP_VERSION__}
      </footer>
      <NotificationToast />
      <ShutdownOverlay />
    </div>
  );
}
